import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { apiErrorMessage, hrService, HRVacation } from '../../services/hrService';
import { formatDateBR } from '../../utils/date';
import { VACATION_STATUS_COLORS, VACATION_STATUS_LABELS, VacationStatus } from '../../types/collaborator';

const TABS: { label: string; status?: VacationStatus }[] = [
    { label: 'Pendentes', status: 'PENDING' },
    { label: 'Aprovadas', status: 'APPROVED' },
    { label: 'Recusadas', status: 'REJECTED' },
    { label: 'Todas' },
];

const FeriasAprovacoesList: React.FC = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState(0);
    const [items, setItems] = useState<HRVacation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [decision, setDecision] = useState<{ item: HRVacation; status: VacationStatus } | null>(null);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [decisionError, setDecisionError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setItems(await hrService.listVacations(TABS[tab].status));
            setError(null);
        } catch (err) {
            setError(apiErrorMessage(err, 'Erro ao carregar as solicitações de férias.'));
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const openDecision = (item: HRVacation, status: VacationStatus) => {
        setNotes('');
        setDecisionError(null);
        setDecision({ item, status });
    };

    const confirm = async () => {
        if (!decision) return;
        try {
            setSaving(true);
            await hrService.decideVacation(decision.item.id, decision.status, notes);
            setSuccess(`Férias de ${decision.item.user_name}: ${VACATION_STATUS_LABELS[decision.status].toLowerCase()}.`);
            setDecision(null);
            load();
        } catch (err) {
            setDecisionError(apiErrorMessage(err, 'Erro ao registrar a decisão.'));
        } finally {
            setSaving(false);
        }
    };

    const titles: Record<string, string> = { APPROVED: 'Aprovar férias', REJECTED: 'Recusar férias', CANCELLED: 'Cancelar férias aprovadas' };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Aprovações de férias</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
                {TABS.map((t) => <Tab key={t.label} label={t.label} />)}
            </Tabs>

            {loading && <LinearProgress sx={{ mb: 1 }} />}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Colaborador</TableCell>
                            <TableCell>Período</TableCell>
                            <TableCell>Dias</TableCell>
                            <TableCell>Observação</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const own = item.user_id === user?.id;
                            return (
                                <TableRow key={item.id} hover>
                                    <TableCell>{item.user_name}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {formatDateBR(item.start_date)} a {formatDateBR(item.end_date)}
                                        {item.conflicts.length > 0 && (
                                            <Tooltip title={`Também de férias no período: ${item.conflicts.join(', ')}`}>
                                                <WarningIcon fontSize="small" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }}
                                                    aria-label={`Conflito com ${item.conflicts.join(', ')}`} />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>{item.days}</TableCell>
                                    <TableCell>
                                        {item.notes || '-'}
                                        {item.review_notes && (
                                            <Typography variant="caption" color="text.secondary" display="block">Parecer: {item.review_notes}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={VACATION_STATUS_LABELS[item.status]} color={VACATION_STATUS_COLORS[item.status]} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                            {own && item.status === 'PENDING' && (
                                                <Typography variant="caption" color="text.secondary">Seu pedido: outra pessoa aprova</Typography>
                                            )}
                                            {!own && item.status === 'PENDING' && (
                                                <>
                                                    <Button size="small" variant="contained" color="success" onClick={() => openDecision(item, 'APPROVED')}>Aprovar</Button>
                                                    <Button size="small" color="error" onClick={() => openDecision(item, 'REJECTED')}>Recusar</Button>
                                                </>
                                            )}
                                            {!own && item.status === 'APPROVED' && (
                                                <Button size="small" color="error" onClick={() => openDecision(item, 'CANCELLED')}>Cancelar</Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} align="center">Nenhuma solicitação nesta situação.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!decision} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{decision ? titles[decision.status] : ''}</DialogTitle>
                <DialogContent>
                    {decision && (
                        <Typography gutterBottom>
                            {decision.item.user_name}: {formatDateBR(decision.item.start_date)} a {formatDateBR(decision.item.end_date)} ({decision.item.days} dias)
                        </Typography>
                    )}
                    {decision && decision.status === 'APPROVED' && decision.item.conflicts.length > 0 && (
                        <Alert severity="warning" sx={{ my: 1 }}>Também estarão de férias: {decision.item.conflicts.join(', ')}.</Alert>
                    )}
                    {decisionError && <Alert severity="error" sx={{ my: 1 }}>{decisionError}</Alert>}
                    <TextField fullWidth multiline minRows={3} margin="normal" value={notes} onChange={(e) => setNotes(e.target.value)}
                        label={decision?.status === 'REJECTED' ? 'Motivo da recusa (obrigatório)' : 'Observação (opcional)'}
                        helperText="O colaborador vê este parecer na tela de férias" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDecision(null)}>Voltar</Button>
                    <Button variant="contained" color={decision?.status === 'APPROVED' ? 'primary' : 'error'} onClick={confirm}
                        disabled={saving || (decision?.status === 'REJECTED' && !notes.trim())}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FeriasAprovacoesList;
